// ============================================================================
// TasksFields — liste dynamique des « taches principales » (E1d, max 3)
// ============================================================================
// Portage maison du PoC components/postings/tasks-fields.tsx : chaque ligne est un
// champ controle ; le parent recoit le tableau via onChange (les lignes vides sont
// ignorees a la soumission). Partage entre le dialogue de creation et le panneau
// d'edition. Tokens @gs/ui uniquement (regle #28) — zero valeur CSS en dur.

import { useId } from 'react';
import { Plus, X } from 'lucide-react';
import { GS_COMPONENTS, GsIconButton } from '@gs/ui';
import { useTranslation } from '../../i18n';

const MAX_TASKS = 3;

export function TasksFields({
  tasks,
  onChange,
}: {
  /** Lignes courantes (au moins une ligne vide pour la saisie). */
  tasks: string[];
  onChange: (tasks: string[]) => void;
}) {
  const { t } = useTranslation();
  const baseId = useId();
  const rows = tasks.length > 0 ? tasks : [''];

  function update(index: number, value: string) {
    onChange(rows.map((row, i) => (i === index ? value : row)));
  }

  function remove(index: number) {
    const next = rows.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : ['']);
  }

  function add() {
    if (rows.length < MAX_TASKS) onChange([...rows, '']);
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-meta font-medium text-slate-600 dark:text-slate-300">
        {t('gest:affichages.form.tasksLabel')}
      </span>
      {rows.map((task, i) => (
        <div key={`${baseId}-${i}`} className="flex items-center gap-2">
          <input
            aria-label={t('gest:affichages.form.taskAria', { n: i + 1 })}
            value={task}
            onChange={(e) => update(i, e.target.value)}
            maxLength={200}
            placeholder={t('gest:affichages.form.taskPlaceholder', { n: i + 1 })}
            className={`${GS_COMPONENTS.input} flex-1`}
          />
          <GsIconButton
            icon={<X size={15} aria-hidden="true" />}
            tooltip={t('gest:affichages.form.taskRemove')}
            aria-label={t('gest:affichages.form.taskRemove')}
            size="sm"
            variant="ghost"
            onClick={() => remove(i)}
          />
        </div>
      ))}
      {rows.length < MAX_TASKS ? (
        <button
          type="button"
          onClick={add}
          className={`${GS_COMPONENTS.btnGhost} inline-flex w-fit touch-manipulation items-center gap-1.5`}
        >
          <Plus size={16} aria-hidden="true" />
          {t('gest:affichages.form.taskAdd')}
        </button>
      ) : null}
    </div>
  );
}
